function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import PropTypes from 'prop-types';
import React from 'react';
import { Image as RNImage } from 'react-native';
import { getSSRImageUrl } from '@dkrm/general-libs/Utils/imageUtils';
import { makeCancellable } from '@dkrm/general-libs/Utils/promise';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

/**
 * This component wraps rnw Image
 * Design Decision:
 * - Automatically transfers prop source to defaultSource for quick image loading
 */
class Image extends React.Component {
  constructor(props) {
    super(props);
    const blurRadius = props.shouldPreloadSmallerVersion ? props.initialBlurRadius : 0;
    this.state = {
      clientSource: undefined,
      blurRadius,
      isSourceCached: false
    };
  }
  componentDidMount() {
    this.loadResultingImage();
  }
  shouldComponentUpdate(nextProps, nextState) {
    return get(this.props, 'source.uri') !== get(nextProps, 'source.uri') || !isEqual(this.state, nextState);
  }
  componentDidUpdate(prevProps) {
    if (get(prevProps, 'source.uri') !== get(this.props, 'source.uri')) {
      this.loadResultingImage();
    }
  }
  componentWillUnmount() {
    this.cancelPrefetchImage();
    this.cancelQueryCache();
  }
  prefetchImage(imageUrl) {
    this.cancelPrefetchImage();
    this._prefetchImage = makeCancellable(RNImage.prefetch(imageUrl));
    return this._prefetchImage;
  }
  cancelPrefetchImage() {
    if (this._prefetchImage) {
      this._prefetchImage.cancel();
    }
  }
  queryCache(uris) {
    this.cancelQueryCache();
    this._queryCache = makeCancellable(RNImage.queryCache(uris));
    return this._queryCache;
  }
  cancelQueryCache() {
    if (this._queryCache) {
      this._queryCache.cancel();
    }
  }
  async loadResultingImage() {
    try {
      const {
        source
      } = this.props;
      const imageCache = await this.queryCache([source.uri]);
      const isSourceCached = !!imageCache[source.uri];
      if (!isSourceCached) {
        await this.prefetchImage(source.uri);
      }
      this.setState({
        blurRadius: 0,
        clientSource: source,
        isSourceCached
      });
    } catch (err) {
      // catch image source not found
    }
  }
  render() {
    const {
      source,
      defaultSource,
      shouldPreloadSmallerVersion,
      smallerVersionRatio,
      initialBlurRadius,
      forwardedRef,
      ...rest
    } = this.props;

    /** generate default source */
    const {
      clientSource,
      blurRadius,
      isSourceCached
    } = this.state;
    const ssrSource = {
      ...(source || {})
    };
    if (!isSourceCached) {
      ssrSource.uri = getSSRImageUrl(ssrSource.uri, ssrSource.width, ssrSource.height, (global.__MOBILE__ || !global.__SSR__) && shouldPreloadSmallerVersion ? 0.1 : smallerVersionRatio);
    }
    return /*#__PURE__*/React.createElement(RNImage, _extends({
      ref: forwardedRef,
      blurRadius: blurRadius,
      defaultSource: defaultSource || ssrSource,
      source: clientSource
    }, rest));
  }
}
Image.defaultProps = {
  source: undefined,
  defaultSource: undefined,
  shouldPreloadSmallerVersion: true,
  smallerVersionRatio: 0.8,
  initialBlurRadius: 12,
  forwardedRef: null
};
Image.propTypes = process.env.NODE_ENV !== "production" ? {
  source: PropTypes.shape(),
  defaultSource: PropTypes.shape(),
  shouldPreloadSmallerVersion: PropTypes.bool,
  smallerVersionRatio: PropTypes.number,
  initialBlurRadius: PropTypes.number,
  forwardedRef: PropTypes.shape()
} : {};
export default /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(Image, _extends({}, props, {
  forwardedRef: ref
})));